import { Box, Button } from '@customink/pigment-react';
import type { ButtonProps } from '@customink/pigment-react/lib/components/Button/Button';
import { DialogActions } from '@customink/pigment-react/lib/components/DialogLegacy';
import { useMobileBreakpoint } from '@customink/project-service-react-components';
import { useTheme } from '@mui/material/styles';
import type { FormEventHandler } from 'react';

import { spacing32, spacing8 } from '@/utils/designTokens';

type ButtonVariant = NonNullable<ButtonProps<'button'>['variant']>;

type ButtonAttributes = {
  label: string;
  href?: string;
  onClick?: FormEventHandler | (() => void);
};

export type ButtonActions = { [key in ButtonVariant]?: ButtonAttributes };

export default function ButtonSet({
  actions,
  centered,
  disableMobileStyles,
  inline,
  alignButtons = 'left',
}: {
  actions?: ButtonActions;
  centered?: boolean;
  disableMobileStyles?: boolean;
  inline?: boolean;
  alignButtons?: 'left' | 'right';
}) {
  const isMobile = useMobileBreakpoint();
  const theme = useTheme();
  const applyMobileStyles = isMobile && !disableMobileStyles;

  if (!actions) return <Box paddingBottom={spacing32} />;

  const buttonsInOrder = (
    ['link', 'tertiary', 'secondary', 'primary'] as const
  ).map((variant: ButtonVariant) => {
    const action = actions[variant];
    if (!action) return null;
    return (
      <Button
        data-testid={`button-${variant}`}
        fullWidth={applyMobileStyles}
        href={action.href}
        key={variant}
        onClick={action.onClick}
        sx={{ ...(applyMobileStyles ? {} : { width: 'unset' }) }}
        type={variant === 'primary' ? 'submit' : 'button'}
        variant={variant}
      >
        {action.label}
      </Button>
    );
  });

  const dialogActionsSx = {
    display: 'flex',
    flexWrap: centered || applyMobileStyles ? 'wrap' : 'nowrap',
    padding: inline ? '0' : '16px 24px 0',
    justifyContent: centered ? 'center' : 'inherit',
  };

  return (
    <DialogActions sx={dialogActionsSx}>
      <Box
        display="flex"
        flexDirection={centered || applyMobileStyles ? 'column-reverse' : 'row'}
        gap={spacing8}
        justifyContent={alignButtons === 'left' ? 'flex-start' : 'flex-end'}
        width="100%"
      >
        {buttonsInOrder}
      </Box>
    </DialogActions>
  );
}
